"use client";

import { generateSearchParams, getLastFlightSearch } from "@/feature/flight/search/params/flight-search-persistence";
import { encodedHolidayParams } from "@/feature/holiday/search/lib/holiday-formatter-helper";
import { getLastHolidaySearch } from "@/feature/holiday/search/params/holiday-search-persistence";
import { encodedHotelParams } from "@/feature/hotel/search/lib/hotel-formatter-helper";
import { getLastHotelSearch } from "@/feature/hotel/search/params/hotel-search-persistence";
import { encodedVisaParams } from "@/feature/visa/search/lib/visa-formatter-helper";
import { getLastVisaSearch } from "@/feature/visa/search/params/visa-search-persistence";

export type SearchTabId =
  | "flights"
  | "hotels"
  | "visa"
  | "holidays"
  | "umrah"
  | "airport_services";

export function isExternalSearchTab(tabId: SearchTabId): boolean {
  return tabId === "airport_services";
}

export function getSearchTabFallbackHref(tabId: SearchTabId): string {
  if (tabId === "airport_services") return "https://sohi.com.bd/";
  if (tabId === "umrah") return "/umrah";
  if (tabId === "holidays") return "/holiday";
  return `/?tab=${tabId}`;
}

function hasHolidaySearchCache() {
  const search = getLastHolidaySearch();
  if (!search) {
    return null;
  }

  const params = search.city?.city_id && search.city.city
    ? encodedHolidayParams({
        city_id: String(search.city.city_id),
        city: search.city.city,
        country: search.city.country_name,
      })
    : "";

  return params ? `/holiday?${params}` : "/holiday";
}

function hasVisaSearchCache() {
  const search = getLastVisaSearch();
  if (!search) {
    return null;
  }

  const params = encodedVisaParams(search);
  return params ? `/visa?${params}` : "/visa";
}

export function resolveSearchTabHref(tabId: SearchTabId): string {
  const fallbackHref = getSearchTabFallbackHref(tabId);

  switch (tabId) {
    case "flights": {
      const search = getLastFlightSearch();
      if (!search) return fallbackHref;

      const params = generateSearchParams(search);
      return params ? `/flights${params}` : fallbackHref;
    }
    case "hotels": {
      const search = getLastHotelSearch();
      return search ? `/hotels?${encodedHotelParams(search)}` : fallbackHref;
    }
    case "visa":
      return hasVisaSearchCache() ?? fallbackHref;
    case "holidays":
      return hasHolidaySearchCache() ?? fallbackHref;
    case "umrah":
    case "airport_services":
    default:
      return fallbackHref;
  }
}
